import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import { useDeepCompareEffect } from "@react-hookz/web";
import { isEmpty } from "lodash-es";
import { useCallback, useEffect, useReducer, useState, type FC } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { extractZodTreeErrorProps } from "../../../../utils/errors/extract-zod-tree-error-props";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import { useEnabledChannels } from "../../../channels/hooks/use-enabled-channels";
import type { MomentFormMoment } from "../../../moment/types/moment-form-moment";
import { useSegmentFields } from "../../hooks/use-segment-fields";
import { useSegmentSaveAllowed } from "../../hooks/use-segment-save-allowed";
import { makeDefaultState, segmentReducer } from "../manager-v2/reducer/reducer";
import { SegmentManager } from "../manager-v2/segment-manager";
import { SegmentPreview } from "../manager-v2/segment-preview";
import {
  recipientTypeSchemaNonRequiredName,
  segmentSchemaNonRequiredName,
} from "../manager-v2/types";
import { segmentJsonHasErrors } from "../manager-v2/utils/errors";
import type { SegmentFiltersV3Props } from "./types";

export const SegmentFiltersV3: FC<SegmentFiltersV3Props> = ({
  selectedSegment,
  disabled,
  showValidationErrors,
  isNew,
  onConfirm,
}) => {
  const { control, getValues } = useFormContext<MomentFormMoment>();

  useWatch({ control, name: ["channel"] });
  const [selectedChannel] = getValues(["channel"]);
  const { loading: segmentFieldsLoading, data: segmentFields } = useSegmentFields();
  const { data: channels, loading: channelsLoading } = useEnabledChannels();
  const { data: segment_save_allowed } = useSegmentSaveAllowed();

  const fieldsDialog = useDialog();

  const { handleOpen: handleFieldsOpen } = fieldsDialog;

  useEffect(() => {
    if (isNew) handleFieldsOpen();
  }, [isNew, handleFieldsOpen]);

  const mappedSegment = {
    ...selectedSegment,
    description: selectedSegment.description ?? "",
    is_default: selectedSegment.is_default ?? false,
    segment_json: selectedSegment.segment_json,
    name: selectedSegment.name ?? "",
  };

  const [shouldSave, setShouldSave] = useState(!isEmpty(mappedSegment.name));

  const [state, dispatch] = useReducer(
    segmentReducer,
    makeDefaultState(
      mappedSegment.is_recipient_type
        ? recipientTypeSchemaNonRequiredName
        : segmentSchemaNonRequiredName,
      mappedSegment,
    ),
  );

  useDeepCompareEffect(() => {
    dispatch({
      type: "RESET",
      payload: makeDefaultState(
        mappedSegment.is_recipient_type
          ? recipientTypeSchemaNonRequiredName
          : segmentSchemaNonRequiredName,
        mappedSegment,
      ),
    });
  }, [mappedSegment]);

  useEffect(() => {
    dispatch({
      type: "SET_SCHEMA",
      payload: mappedSegment.is_recipient_type
        ? recipientTypeSchemaNonRequiredName
        : segmentSchemaNonRequiredName,
    });
  }, [mappedSegment.is_recipient_type]);

  useEffect(() => {
    dispatch({ type: "SET_ERRORS_DISPLAY", payload: showValidationErrors });
  }, [showValidationErrors]);

  const handleFieldsConfirm = useCallback(() => {
    if (disabled) return fieldsDialog.handleClose();

    if (state.errors.errors) {
      console.log("Errors exist, cannot save:", state.errors.errors);
      dispatch({ type: "SET_ERRORS_DISPLAY", payload: true });
      return;
    }

    dispatch({ type: "SET_ERRORS_DISPLAY", payload: false });

    fieldsDialog.handleClose();

    const newSegment = {
      ...state.segment,
      created_at: selectedSegment.created_at,
      deleted_at: selectedSegment.deleted_at,
      recipient_type_key: null,
      conditions: [],
      name: shouldSave ? state.segment.name : "",
      descriptions: shouldSave ? state.segment.description : "",
    };

    onConfirm(newSegment);
  }, [
    disabled,
    state.errors.errors,
    fieldsDialog,
    onConfirm,
    state.segment,
    selectedSegment.created_at,
    selectedSegment.deleted_at,
    shouldSave,
  ]);

  if (segmentFieldsLoading || channelsLoading) return <LinearProgress />;

  return (
    <>
      <Stack spacing={1} sx={{ pt: 2 }}>
        {state.errors.errors && state.errors.display && (
          <Alert severity="error">Segment invalid, click below to edit</Alert>
        )}
        <Box>
          <Button
            data-analytics-id={`moment-form-segment-${disabled || !segment_save_allowed ? "view" : "edit"}`}
            color={state.errors.errors ? "error" : "primary"}
            onClick={() => fieldsDialog.handleOpen()}
            variant="contained"
            sx={{ minWidth: 170 }}
          >
            {disabled || !segment_save_allowed ? "View Segment" : "Edit Segment"}
          </Button>
        </Box>
      </Stack>
      <Dialog
        open={fieldsDialog.open}
        onClose={() => fieldsDialog.handleClose()}
        fullWidth
        maxWidth="lg"
        data-testid="segment-fields-dialog"
      >
        <DialogTitle>Segment builder</DialogTitle>
        <DialogContent>
          <SegmentManager
            state={state}
            dispatch={dispatch}
            fields={segmentFields ?? []}
            channels={selectedChannel ? [selectedChannel] : (channels ?? [])}
            disabled={disabled ?? false}
            mode={mappedSegment.is_recipient_type ? "AUDIENCE" : "SEGMENT"}
            showPreview={true}
          />
          {!segmentJsonHasErrors(state.errors) &&
            state.segment.segment_json.segments.length > 1 && (
              <SegmentPreview
                segment={state.segment.segment_json}
                channels={channels ?? []}
                type="full"
              />
            )}
          {isEmpty(state.segment.name) && (
            <Paper sx={{ p: 2, mt: 2, bgcolor: "background.default" }}>
              <FormControlLabel
                value="shouldSave"
                control={
                  <Switch
                    color="primary"
                    checked={shouldSave}
                    onChange={(e) => setShouldSave(e.target.checked)}
                    data-testid="segment-save-switch"
                  />
                }
                label="Save this segment to re-use in other Communications"
                labelPlacement="start"
              />
            </Paper>
          )}
          {shouldSave && (
            <Stack direction="column" spacing={2} sx={{ mt: 2 }}>
              <TextField
                size="small"
                variant="outlined"
                label="Name"
                required
                value={state.segment.name}
                onChange={(e) => {
                  dispatch({ type: "SET_NAME", payload: e.target.value });
                }}
                disabled={disabled}
                {...extractZodTreeErrorProps(state.errors, ["name"])}
              />
              <TextField
                size="small"
                variant="outlined"
                label="Description"
                value={state.segment.description ?? ""}
                onChange={(e) => {
                  dispatch({ type: "SET_DESCRIPTION", payload: e.target.value });
                }}
                multiline
                disabled={disabled}
                {...extractZodTreeErrorProps(state.errors, ["description"])}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            data-testid="segment-form-continue"
            data-analytics-id="segment-form-continue"
            color="primary"
            variant="contained"
            onClick={() => handleFieldsConfirm()}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
